import { maybe } from '@passionware/monads';
import { ApiClient } from 'lib/api/ApiClient.types';
import { isUndefined, omitBy, pick } from 'lodash';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import type { EnumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import {
  buildFilterConverter,
  convertEnumFilterToApi
} from 'v5/platform/filters/filterConverters';
import type {
  FinancialDocument,
  FinancialDocumentInvoice,
  FinancialDocumentReceipt,
  ReconciliationEntityType,
  ReconciliationExpenseLineItem,
  ReconciliationLineItem,
  ReconciliationLineItemBooking,
  ReconciliationLineItemExpense
} from './reconciliation.api';
import {
  computeDocumentsStatus,
  ReconciliationApi,
  ReconciliationFilters,
  ReconciliationQuery
} from './reconciliation.api';
import {
  reconciliationListResponse$,
  type InvoiceOrReceiptRaw,
  type ReconciliationLineItemRaw
} from './reconciliation.api.http.schema';

/** Backend entity_type for expense (37); booking is 27. Used when mapping raw documents. */
const ENTITY_TYPE_EXPENSE = 37;

function documentEntityTypeFromNumber(n: number): ReconciliationEntityType {
  return n === ENTITY_TYPE_EXPENSE ? 'expense' : 'booking';
}

function lineItemEntityTypeToType(
  entityType: number | 'booking' | 'expense'
): ReconciliationEntityType {
  if (typeof entityType === 'string') return entityType;
  return entityType === ENTITY_TYPE_EXPENSE ? 'expense' : 'booking';
}

/**
 * Deduplicate by doc.id and prefer the doc that has `file` so we don't lose file info
 * when the API returns the same document twice (e.g. with and without file expanded).
 */
function deduplicateByIdPreferFile(
  docs: InvoiceOrReceiptRaw[]
): InvoiceOrReceiptRaw[] {
  const byId = new Map<number, InvoiceOrReceiptRaw>();
  for (const doc of docs) {
    const existing = byId.get(doc.id);
    const preferThis = !existing || (doc.file != null && existing.file == null);
    if (preferThis) {
      byId.set(doc.id, doc);
    }
  }
  return Array.from(byId.values());
}

function mapInvoiceOrReceiptsToFinancialDocuments(
  docs: InvoiceOrReceiptRaw[],
  expenses: ReconciliationLineItemExpense[],
  options: {
    lineItemType: ReconciliationLineItem['type'];
    lineItemId: number;
  }
): FinancialDocument[] {
  const expenseList = expenses ?? [];
  const { lineItemType, lineItemId } = options;

  const allowedReceiptIds =
    lineItemType === 'booking'
      ? new Set(expenseList.flatMap(e => e.receiptIds ?? []))
      : null;

  const filtered =
    lineItemType === 'booking' && allowedReceiptIds
      ? docs.filter(doc => {
          const docType = documentEntityTypeFromNumber(doc.entity_type);
          if (docType === 'booking') {
            return doc.entity_id === lineItemId;
          }
          if (docType === 'expense') {
            return allowedReceiptIds.has(doc.id);
          }
          return false;
        })
      : lineItemType === 'expense'
        ? docs.filter(
            doc =>
              documentEntityTypeFromNumber(doc.entity_type) === 'expense' &&
              doc.entity_id === lineItemId
          )
        : docs;

  const deduplicated = deduplicateByIdPreferFile(filtered);

  return deduplicated.map(doc => {
    const docType = documentEntityTypeFromNumber(doc.entity_type);
    const base = {
      id: doc.id,
      entity_id: doc.entity_id,
      document_data: {
        amount: doc.document_data?.amount ?? 0,
        currency: doc.document_data?.currency,
        reference: doc.document_data?.reference
      },
      ...omitBy(pick(doc, ['status', 'file_id', 'file']), isUndefined)
    };
    if (docType === 'booking') {
      return { ...base, type: 'booking' } as FinancialDocumentInvoice;
    }
    const expense = expenseList.find(e => e.expenseId === doc.entity_id);
    const expenseInfo = expense
      ? {
          expenseId: expense.expenseId,
          name: expense.name,
          categoryName: expense.categoryName,
          vendor: expense.vendor ?? null
        }
      : undefined;
    return {
      ...base,
      type: 'expense',
      ...(expenseInfo && { expenseInfo })
    } as FinancialDocumentReceipt;
  });
}

function normalizeLineItem(
  raw: ReconciliationLineItemRaw
): ReconciliationLineItem {
  const type = lineItemEntityTypeToType(raw.entityType);
  const expenses = raw.expenses.map(e => ({
    expenseId: e.expenseId,
    name: e.name ?? null,
    categoryName: e.categoryName ?? null,
    vendor: e.vendor ?? null,
    receiptIds: e.receiptIds
  }));
  const entityIdForFilter =
    type === 'booking' ? raw.id : (raw.expenseId ?? raw.id);
  const financialDocuments = mapInvoiceOrReceiptsToFinancialDocuments(
    raw.invoiceOrReceipts ?? [],
    expenses,
    {
      lineItemType: type,
      lineItemId: entityIdForFilter
    }
  );

  const vendor = raw.resource.vendor ?? null;
  const role = raw.resource.role ?? null;
  const organisation = raw.resource.organisation ?? null;

  const documentsStatus = computeDocumentsStatus(
    financialDocuments,
    raw.budgeted.actual,
    raw.budgeted.committed
  );

  if (type === 'booking') {
    return {
      id: raw.id,
      assignmentId: raw.assignmentId ?? null,
      updatedAt: raw.updatedAt ?? null,
      currency: raw.currency ?? '',
      vendor,
      role,
      organisation,
      type: 'booking',
      production: raw.production,
      resource: {
        id: raw.resource.id,
        resourceTypeId: raw.resource.resource_type_id ?? null,
        pronounsOptionId: raw.resource.pronouns_option_id ?? null,
        booking_status_id: raw.resource.booking_status_id
      },
      budgeted: raw.budgeted,
      financialDocuments,
      documentsStatus,
      bookingId: raw.bookingId ?? 0,
      expenseId: null,
      productionBudgetExpenseId: null,
      expenses: raw.expenses ?? []
    } as ReconciliationLineItemBooking;
  }

  return {
    id: raw.id,
    assignmentId: null,
    updatedAt: raw.updatedAt ?? null,
    currency: raw.currency ?? '',
    vendor,
    role,
    organisation,
    type: 'expense',
    production: raw.production,
    budgeted: raw.budgeted,
    financialDocuments,
    documentsStatus,
    bookingId: null,
    expenseId: raw.expenseId ?? raw.id,
    productionBudgetExpenseId: raw.productionBudgetExpenseId ?? null,
    expenses: raw.expenses ?? []
  } as ReconciliationExpenseLineItem;
}

// Map frontend sort fields to backend field names (backend expects snake_case)
const mapReconciliationSortFieldToApi = (field: string): string => {
  const fieldMap: Record<string, string> = {
    updated_at: 'updated_at',
    booking_status: 'booking_status'
  };
  return fieldMap[field] ?? 'updated_at';
};

// Convert sorter to API format
const convertReconciliationSorterToApi = (sorter: {
  field: string;
  order: 'asc' | 'desc';
}) => ({
  field: mapReconciliationSortFieldToApi(sorter.field),
  direction: sorter.order
});

// Convert reconciliation filters to API format (uses platform filter converters)
const convertReconciliationFiltersToApi =
  buildFilterConverter<ReconciliationFilters>({
    invoice_status: (filter: unknown): unknown | null =>
      maybe.isPresent(filter)
        ? convertEnumFilterToApi(filter as EnumFilter<string>)
        : null,
    production_id: (filter: unknown): unknown | null =>
      maybe.isPresent(filter)
        ? convertEnumFilterToApi(filter as EnumFilter<number>)
        : null,
    resource_id: (filter: unknown): unknown | null =>
      maybe.isPresent(filter)
        ? convertEnumFilterToApi(filter as EnumFilter<number>)
        : null
  });

export function getReconciliationListRequest(
  query: ReconciliationQuery
): Record<string, unknown> {
  const sort = query.sort ?? {
    field: 'updated_at' as const,
    order: 'desc' as const
  };
  const filters = convertReconciliationFiltersToApi(query.filters);

  return {
    query: query.search ?? '',
    p: query.page.page + 1,
    order_by: convertReconciliationSorterToApi(sort),
    filters
  };
}

export function createReconciliationApi(config: {
  client: ApiClient;
}): ReconciliationApi {
  const { client } = config;
  return {
    getList: async (query: ReconciliationQuery, signal) => {
      const requestData = getReconciliationListRequest(query);

      const response = await client.post('/reconciliations', {
        data: requestData,
        token: true,
        signal
      });

      const parsed = parseWithDecoratedError(
        reconciliationListResponse$,
        response
      );
      const items = parsed.results.map(normalizeLineItem);

      return {
        items,
        count: parsed.paging?.total ?? items.length
      };
    }
  };
}
